import {useEffect, useState} from 'react'
import couponFragment from '@data/fragments/checkout/v3/couponFragment.main'
import summaryFragment from '@data/fragments/checkout/v3/summaryFragment.main'
import {EVENTS} from '@helpers/analytics/events'
import {PurchaseTaxonomies} from '@helpers/analytics/events/purchase'
import {sendEvent} from '@helpers/analytics/sendEvents'
import isValidCoupon from '@helpers/misc/isValidCoupon'
import useEventsBaseProperties from '@hooks/useEventsBaseProperties'
import useMessage from '@hooks/useMessage'
import useOptimisticCheckout from '@page-components/CheckoutV3/hooks/useOptimisticCheckout'
import {useMutate} from 'apollo-hooks'
import gql from 'graphql-tag'

export interface UseCouponPrefs {
  websiteId?: string
  couponCode?: string
  menuId?: string
  cart?: {
    couponStatus?: {
      errorMessage?: string
      couponId?: string
      name?: string
      discount?: number
    }
  }
}

export default function useCoupon(preferences: UseCouponPrefs) {
  const [loading, setLoading] = useState(false)
  const [submittedThisSession, setSubmittedThisSession] = useState(false)
  const {optimisticStart, optimisticEnd} = useOptimisticCheckout()
  const baseEventProperties = useEventsBaseProperties()

  const couponCode = preferences?.couponCode ?? ''
  const showMessage = useMessage()
  const mutate = useMutate()

  const couponStatus = preferences?.cart?.couponStatus ?? null
  const errorMessage = preferences?.cart?.couponStatus?.errorMessage ?? null

  const couponExists = isValidCoupon(preferences)

  const submit = async ({code}) => {
    optimisticStart()
    setLoading(true)
    try {
      await mutate({
        mutation: gql`
          mutation applyCouponCheckoutV3($websiteId: ID!, $couponCode: String) {
            setUserPreferences(websiteId: $websiteId, couponCode: $couponCode) {
              _id
              ...checkoutV3CouponPreferences
              ...checkoutV3SummaryPreferences
            }
          }
          ${couponFragment}
          ${summaryFragment}
        `,
        variables: {
          websiteId: preferences.websiteId,
          couponCode: code || null
        }
      })

      // avoid recording coupons discarding as an event
      if (code) {
        setSubmittedThisSession(true)
      }
    } catch (error) {
      showMessage(error)
    }
    optimisticEnd()
    setLoading(false)
  }

  useEffect(() => {
    if (errorMessage) {
      sendEvent<PurchaseTaxonomies['couponAdded']>(EVENTS.purchase.couponAdded, {
        couponDiscount: couponStatus.discount ?? 0,
        couponCode,
        couponName: couponStatus.name,
        couponId: couponStatus.couponId,
        errorMessage: errorMessage ?? '',
        menuId: preferences?.menuId ?? '',
        ...baseEventProperties
      })
    }
  }, [errorMessage])

  useEffect(() => {
    if (!couponExists || errorMessage || !submittedThisSession || !couponCode) return

    sendEvent<PurchaseTaxonomies['couponAdded']>(EVENTS.purchase.couponAdded, {
      couponDiscount: couponStatus?.discount ?? 0,
      couponCode,
      couponName: couponStatus?.name,
      couponId: couponStatus?.couponId,
      errorMessage: errorMessage ?? '',
      menuId: preferences?.menuId ?? '',
      ...baseEventProperties
    })
  }, [couponExists, errorMessage, submittedThisSession])

  return {
    submit,
    errorMessage,
    couponExists,
    loading,
    submittedThisSession
  }
}
